import React, { useState, useEffect } from 'react';
import {
  Finding,
  FindingDashboardStats,
  FindingStatus,
  FindingSeverity,
  FindingType,
} from './types';
import { findingService } from './findingService';
import { FindingListTable } from './components/FindingListTable';
import { FindingDetailModal } from './components/FindingDetailModal';
import { CreateFindingModal } from './components/CreateFindingModal';

export const FindingsPage: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState<'ALL' | FindingStatus>('ALL');
  const [filterSeverity, setFilterSeverity] = useState<'ALL' | FindingSeverity>('ALL');
  const [filterType, setFilterType] = useState<'ALL' | FindingType>('ALL');

  const [stats, setStats] = useState<FindingDashboardStats | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [statsData, findingsData] = await Promise.all([
        findingService.getDashboardStats(),
        findingService.getFindings(),
      ]);
      setStats(statsData);
      setFindings(findingsData);
    } catch (err) {
      console.error('Error loading findings data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY MANAGEMENT</div>
        <h1>Quality Findings & Deviations Register</h1>
        <p>Audit and inspection non-conformances, procedural deviations, 5-Why root cause analysis, independent verification (SoD), and closed-loop control.</p>
      </div>

      {/* KPI Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Findings</div>
            <div className="stat-value">{stats.totalFindings}</div>
            <div className="stat-sub">Registered non-conformances</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open Findings</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats.openFindings}</div>
            <div className="stat-sub">Active workflow</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Critical & Major</div>
            <div className="stat-value" style={{ color: '#ef4444' }}>{stats.criticalFindings}</div>
            <div className="stat-sub">Level 1 & 2 items</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Overdue</div>
            <div className="stat-value" style={{ color: '#e11d48' }}>{stats.overdueFindings}</div>
            <div className="stat-sub">Past target date</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Pending Verification</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats.pendingVerification}</div>
            <div className="stat-sub">Awaiting verification</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Closed & Verified</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{stats.closedFindings}</div>
            <div className="stat-sub">Closed and verified</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Repeat Findings</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{stats.repeatFindingsCount}</div>
            <div className="stat-sub">Systemic recurrences</div>
          </div>
        </div>
      )}

      {/* Main Table */}
      <FindingListTable
        findings={findings}
        loading={loading}
        filterStatus={filterStatus}
        filterSeverity={filterSeverity}
        filterType={filterType}
        onFilterStatusChange={setFilterStatus}
        onFilterSeverityChange={setFilterSeverity}
        onFilterTypeChange={setFilterType}
        onSelectFinding={(f) => setSelectedFinding(f)}
        onOpenCreate={() => setShowCreateModal(true)}
      />

      {/* Modals */}
      {selectedFinding && (
        <FindingDetailModal
          finding={selectedFinding}
          onClose={() => setSelectedFinding(null)}
          onRefresh={loadAllData}
        />
      )}

      {showCreateModal && (
        <CreateFindingModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadAllData();
          }}
        />
      )}
    </div>
  );
};

export default FindingsPage;
